import React, { useState } from 'react';
import { Flame, Check, Zap, Trophy } from 'lucide-react';
import type { Contact, DailyCheckIn as DailyCheckInType } from '../../data/go2LegendeMock';

interface DailyCheckInProps {
  user: Contact;
  checkInHistory: DailyCheckInType[];
}

export const DailyCheckIn: React.FC<DailyCheckInProps> = ({ user, checkInHistory }) => {
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const alreadyCheckedIn = user.last_check_in === today;

  const streakDay = (user.check_in_streak % 7) || 7;

  // Calculate points based on streak day
  const getPointsForStreakDay = (day: number): number => {
    if (day === 7) return 25;
    if (day >= 6) return 15;
    if (day >= 3) return 10;
    return 5;
  };

  const todayPoints = getPointsForStreakDay(streakDay);

  const handleCheckIn = () => {
    if (alreadyCheckedIn) return;

    setIsCheckedIn(true);
    setShowSuccess(true);

    setTimeout(() => {
      setShowSuccess(false);
    }, 3000);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 relative overflow-hidden">
      {/* Confetti animation */}
      {showSuccess && (
        <div className="absolute inset-0 pointer-events-none z-10">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                top: '-10px',
                backgroundColor: ['#EE4877', '#009fe3', '#d4e157', '#10B981'][Math.floor(Math.random() * 4)],
                animationDelay: `${Math.random() * 0.5}s`,
                animationDuration: `${1 + Math.random() * 1}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Dnevni Check-in</h3>
          <p className="text-sm text-gray-500">Čekiraj se svaki dan i osvoji bodove</p>
        </div>
        <Flame className="w-8 h-8 text-orange-500" />
      </div>

      {/* Current Streak */}
      <div className="mb-6 text-center">
        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-50 to-red-50 px-6 py-3 rounded-full">
          <Flame className="w-6 h-6 text-orange-500" />
          <span className="text-2xl font-bold text-gray-900">{user.check_in_streak}</span>
          <span className="text-lg text-gray-700">dana u nizu!</span>
        </div>
      </div>

      {/* Streak Progress */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-3">
          {[1, 2, 3, 4, 5, 6, 7].map((day) => {
            const points = getPointsForStreakDay(day);
            const isCompleted = day < streakDay || (day === streakDay && alreadyCheckedIn);
            const isCurrent = day === streakDay && !alreadyCheckedIn;

            return (
              <div key={day} className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 transition-all ${
                    isCompleted
                      ? 'bg-gradient-to-br from-go2-pink to-pink-600 text-white shadow-md'
                      : isCurrent
                      ? 'bg-gradient-to-br from-go2-blue to-blue-600 text-white shadow-md ring-2 ring-go2-blue ring-offset-2'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {isCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <span className="text-xs font-bold">{day}</span>
                  )}
                </div>
                <span className="text-xs text-gray-500 font-medium">{points}b</span>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-center text-gray-500">
          Danи 1-2: 5 bodova • Dani 3-5: 10 bodova • Dan 6: 15 bodova • Dan 7: 25 bodova
        </p>
      </div>

      {/* Check-in Button */}
      {!alreadyCheckedIn && !showSuccess && (
        <button
          onClick={handleCheckIn}
          className="w-full bg-gradient-to-r from-go2-pink to-pink-600 text-white py-4 px-6 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95 mb-4"
        >
          <span className="flex items-center justify-center gap-2">
            <Zap className="w-6 h-6" />
            CHECK IN
          </span>
        </button>
      )}

      {alreadyCheckedIn && (
        <div className="w-full bg-gray-100 text-gray-500 py-4 px-6 rounded-full font-bold text-lg text-center mb-4 cursor-not-allowed">
          <span className="flex items-center justify-center gap-2">
            <Check className="w-6 h-6" />
            Već si čekiran/a danas!
          </span>
        </div>
      )}

      {showSuccess && (
        <div className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 px-6 rounded-full font-bold text-lg text-center mb-4 shadow-lg">
          <span className="flex items-center justify-center gap-2">
            <Trophy className="w-6 h-6" />
            Čekirano! +{todayPoints} bodova
          </span>
        </div>
      )}

      {/* Streak Bonus Info */}
      <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border border-amber-200 mb-4">
        <div className="flex items-start gap-2">
          <Trophy className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900">7-dnevni Bonus</p>
            <p className="text-xs text-amber-700">
              Ostvari 7 dana u nizu = dupli bodovi na sledeću rezervaciju!
            </p>
          </div>
        </div>
      </div>

      {/* Check-in History */}
      {checkInHistory.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Poslednji check-inovi</h4>
          <div className="space-y-1">
            {checkInHistory.slice(-5).reverse().map((entry, index) => (
              <div
                key={index}
                className="flex items-center justify-between text-xs py-1.5 px-2 bg-gray-50 rounded"
              >
                <div className="flex items-center gap-2">
                  <Check className="w-3 h-3 text-green-500" />
                  <span className="text-gray-600">
                    {new Date(entry.date).toLocaleDateString('sr-RS', {
                      day: 'numeric',
                      month: 'short',
                    })}
                  </span>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-500">Dan {entry.streakDay}</span>
                </div>
                <span className="font-semibold text-go2-pink">+{entry.points} bodova</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Confetti CSS */}
      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(400px) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti 2s ease-out forwards;
        }
      `}</style>
    </div>
  );
};
